import {
  READ_CURRENT_PAGE_MAX_CHARS,
  type ReadCurrentPageOutput,
} from '@realadvisor/common-types';

// Client-side execution of the agent's `read_current_page` tool: the visible
// text of the page the user is looking at, so the agent can answer "these
// leads", "this number", "the points on the map" from what is on screen
// instead of guessing a search. It reads only what the user already sees, as
// the user, so it widens nothing: anonymised contacts stay anonymised.
//
// The page area is the element marked `data-chat-page` (ChatPanelLayout); while
// the chat is open, drawers render inside it too. The mobile chat sheet sits in
// that area and is marked `data-chat-panel`, which is skipped.

const ELEMENT_NODE = 1;
const TEXT_NODE = 3;

// The DOM surface the serializer reads. A real Element satisfies it; tests
// build plain objects.
export type PageNode = {
  nodeType: number;
  nodeValue: string | null;
  childNodes: ArrayLike<PageNode>;
  tagName?: string;
  getAttribute?: (name: string) => string | null;
  checkVisibility?: () => boolean;
  checked?: boolean;
  value?: string;
  type?: string;
};

const SKIPPED_TAGS = new Set([
  'SCRIPT',
  'STYLE',
  'NOSCRIPT',
  'TEMPLATE',
  'SVG',
  'CANVAS',
  'IFRAME',
  'SELECT',
]);

const BLOCK_TAGS = new Set([
  'ADDRESS',
  'ARTICLE',
  'ASIDE',
  'BLOCKQUOTE',
  'BR',
  'DD',
  'DIV',
  'DL',
  'DT',
  'FIELDSET',
  'FIGURE',
  'FOOTER',
  'FORM',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
  'HEADER',
  'HR',
  'LI',
  'NAV',
  'OL',
  'P',
  'SECTION',
  'TABLE',
  'TR',
  'UL',
]);

const HEADING_TAGS = new Set(['H1', 'H2', 'H3', 'H4', 'H5', 'H6']);
const CELL_TAGS = new Set(['TD', 'TH']);
const CELL_ROLES = new Set(['cell', 'gridcell', 'columnheader', 'rowheader']);
const TEXT_INPUT_TYPES = new Set(['text', 'search', 'number', 'email', 'tel']);

// Separators placed while walking, turned into text by `normalizePageText`.
const LINE = '\n';
const CELL = '\t';

const attribute = (node: PageNode, name: string): string | null =>
  node.getAttribute?.(name) ?? null;

// The computed CSS `display` of an element, or null where there is none.
export type DisplayOf = (node: PageNode) => string | null;

// checkVisibility() is false for a `display: contents` wrapper although its
// children render: the MUI data grid wraps its rows in one, so trusting it
// alone dropped every row of the leads list.
const isRendered = (node: PageNode, displayOf: DisplayOf): boolean =>
  node.checkVisibility?.() !== false || displayOf(node) === 'contents';

const isHidden = (node: PageNode, displayOf: DisplayOf): boolean =>
  attribute(node, 'aria-hidden') === 'true' ||
  attribute(node, 'hidden') != null ||
  attribute(node, 'data-chat-panel') != null ||
  !isRendered(node, displayOf);

// An in-app link target (`/fr/leads/<id>`), shown next to the link text so the
// agent gets the record id. External links add nothing it can act on.
const internalPath = (node: PageNode): string | null => {
  const href = attribute(node, 'href');
  return href != null && href.startsWith('/') && !href.startsWith('//')
    ? href
    : null;
};

const inputText = (node: PageNode, tag: string): string | null => {
  if (tag === 'TEXTAREA') {
    return node.value ? `«${node.value}»` : null;
  }
  const type = (node.type ?? 'text').toLowerCase();
  if (type === 'checkbox' || type === 'radio') {
    return node.checked === true ? '[x] ' : '[ ] ';
  }
  return TEXT_INPUT_TYPES.has(type) && node.value ? `«${node.value}»` : null;
};

type Walk = {
  parts: Array<string>;
  length: number;
  limit: number;
  // Inside a table or grid row a block (MUI cells are divs) does not break the
  // line: the row stays one line.
  rowDepth: number;
  displayOf: DisplayOf;
};

const push = (walk: Walk, part: string) => {
  walk.parts.push(part);
  walk.length += part.length;
};

const visit = (node: PageNode, walk: Walk): void => {
  if (walk.length > walk.limit) {
    return;
  }
  if (node.nodeType === TEXT_NODE) {
    const text = (node.nodeValue ?? '').replace(/\s+/g, ' ');
    if (text !== '') {
      push(walk, text);
    }
    return;
  }
  if (node.nodeType !== ELEMENT_NODE || node.tagName == null) {
    return;
  }
  const tag = node.tagName.toUpperCase();
  if (SKIPPED_TAGS.has(tag) || isHidden(node, walk.displayOf)) {
    return;
  }
  if (tag === 'INPUT' || tag === 'TEXTAREA') {
    const text = inputText(node, tag);
    if (text != null) {
      push(walk, text);
    }
    return;
  }

  const role = attribute(node, 'role');
  const isRow = tag === 'TR' || role === 'row';
  const isBlock = isRow || (walk.rowDepth === 0 && BLOCK_TAGS.has(tag));
  const isCell = CELL_TAGS.has(tag) || (role != null && CELL_ROLES.has(role));

  if (isBlock) {
    push(walk, LINE);
  }
  if (isCell) {
    push(walk, CELL);
  }
  if (HEADING_TAGS.has(tag)) {
    push(walk, '## ');
  }
  // A data-grid row the user ticked (MUI marks it aria-selected), and the id
  // of its record: the leads grid opens a lead on row click, so its rows
  // carry no link to read the id from.
  if (role === 'row' && attribute(node, 'aria-selected') === 'true') {
    push(walk, '[selected] ');
  }
  const rowId = role === 'row' ? attribute(node, 'data-id') : null;
  if (rowId != null && rowId !== '') {
    push(walk, `[id ${rowId}] `);
  }
  if (isRow) {
    walk.rowDepth += 1;
  }
  for (let index = 0; index < node.childNodes.length; index += 1) {
    visit(node.childNodes[index], walk);
  }
  if (isRow) {
    walk.rowDepth -= 1;
  }
  const path = tag === 'A' ? internalPath(node) : null;
  if (path != null) {
    push(walk, ` (${path})`);
  }
  if (isCell) {
    push(walk, CELL);
  }
  if (isBlock) {
    push(walk, LINE);
  }
};

// One line per block or row, cells joined by " | ", blank lines dropped.
const normalizePageText = (raw: string): string =>
  raw
    .split(LINE)
    .map(line =>
      line
        .replace(/[  ]+/g, ' ')
        .replace(/^[\s\t|]+|[\s\t|]+$/g, '')
        .replace(/ ?\t[\t ]*/g, ' | '),
    )
    .filter(line => line !== '')
    .join(LINE);

export const serializePageText = (
  root: PageNode,
  {
    maxChars = READ_CURRENT_PAGE_MAX_CHARS,
    displayOf = () => null,
  }: { maxChars?: number; displayOf?: DisplayOf } = {},
): { text: string; truncated: boolean } => {
  // Walk a little past the cap: separators and spaces shrink when normalised.
  const walk: Walk = {
    parts: [],
    length: 0,
    limit: maxChars * 2,
    rowDepth: 0,
    displayOf,
  };
  visit(root, walk);
  const text = normalizePageText(walk.parts.join(''));
  return text.length > maxChars
    ? { text: text.slice(0, maxChars), truncated: true }
    : { text, truncated: false };
};

export const readCurrentPage = (): ReadCurrentPageOutput => {
  const root = document.querySelector('[data-chat-page]');
  if (root == null) {
    return { ok: false, error: 'The page content could not be found.' };
  }
  return {
    ok: true,
    url: `${window.location.pathname}${window.location.search}`,
    ...serializePageText(root, {
      displayOf: node =>
        node instanceof Element ? getComputedStyle(node).display : null,
    }),
  };
};
